import { Component, EventEmitter, Inject, Input, OnInit, OnDestroy, Optional, Output, SimpleChanges, ViewChild } from '@angular/core';
import { DwMetaDataGridComponent, DwAllTextFieldFilterService, DwFilterType } from '@devwareapps/devware-cap';
import { GridApi } from 'ag-grid-community';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { parseAcsCodes } from './parse-acs-codes';
import { DwButtonCommand, DwButtonConfig, DwComponent, DwComponentType, DwExpressionService, DwGridCheckboxSelectionMode, DwGridEvent, DwGridEvents, DwGridHeightMode, DwMessage, DwMessageSeverity, DwMetaDataFormApi, DwMetaDataFormStateService, DwMetaDataGridPreferences, DwMetaDataGridViewPreference, DwMetaDataService, DwMetaDataServiceToken, DwModalConfig, DwModalHandler, DwModalResult, DwQueryHelper, DwSectionBaseComponent, DwUIMetaDataConfig, DwUIMetaDataConfigToken, DwUiConfigRegistryService } from '@devwareapps/devware-cap';
import { Observable, of } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { map } from 'underscore';
import { AppMetaDataLookups, AppMetaDataItemNames, PlanOfActionEntity, PilotExaminerEntity, AcsTestEntity, PlanOfActionSectionEntity, AppMetaData, PlanOfActionSectionTypeAllItems, AcsCodeEntity } from '../../../../meta-data/app-meta-data.service';
import { PlanOfActionRepositoryService } from '../../../plan-of-action/services/plan-of-action-repository.service';
import { GenerateSectionsOptions } from '../../../plan-of-action/models/generate-sections-options.model';
import { PlanOfActionGeneratorService } from '../../../plan-of-action/services/plan-of-action-generator.service';
import { AcsCodeStatuses, AcsCodeStorage, AcsCodesStorageItem } from '../../../plan-of-action/models/acs-code-items.model';
import { GenerateCodesOptions } from '../../../plan-of-action/models/generate-codes-options.model';
import { AcsCodeSelectionModalResult } from '../../../plan-of-action/models/acs-code-selection-modal-options.model';
import { DwMdGridCallbacks } from '@devwareapps/devware-cap/devware-ui-metadata/grid/model/callbacks/dw-md-grid-callbacks.interface';

@Component({
  selector: 'app-select-acs-codes',
  templateUrl: './select-acs-codes.component.html',
  styleUrls: ['./select-acs-codes.component.scss']
})
export class SelectAcsCodesComponent implements OnDestroy {
  @ViewChild('acsCodesGrid') acsCodesGrid: DwMetaDataGridComponent;
  pastedCodesText = '';
  pasteMode = false;
  searchText = '';
  private searchFilter: any;
  private searchSubscription: Subscription;
  private searchTimer: ReturnType<typeof setTimeout>;
  pasteSummary = '';
  pasteBusy = false;
  private pastedCodeIds: number[] = null;
  private pasteSubscription: Subscription;
  private pasteRequest = 0;
  private selectionSync = false;
  private selectionRestorePending = false;
  private pasteSelectionActive = false;
  private selectionTimer: ReturnType<typeof setTimeout>;
  private selectionApi: GridApi;
  private readonly modelUpdated = () => this.scheduleSelectionSync();

  showPasteCodes(): void {
    this.clearSearch();
    this.pasteMode = true;
  }

  searchChanged(): void {
    clearTimeout(this.searchTimer);
    this.searchSubscription?.unsubscribe();
    this.searchTimer = setTimeout(() => {
      const value = this.searchText.trim();
      if (!value) {
        this.clearSearch();
        return;
      }
      const meaning = this.options.useQuestionBank ? AppMetaData.Queries.AcsCodesWithQuestions.QueryMeaning : AppMetaData.Queries.AcsCodesGrid.QueryMeaning;
      this.searchSubscription = this.selectorMetaDataService.getSavedQueryMetaData(this.aosCodesItem, null, meaning)
        .pipe(take(1)).subscribe(metadata => {
          this.searchFilter = this.textSearchService.buildTextFilter(value, DwFilterType.Contains, null, metadata);
          this.setupFilters();
          this.scheduleSelectionSync();
        });
    }, 300);
  }

  clearSearch(): void {
    clearTimeout(this.searchTimer);
    this.searchSubscription?.unsubscribe();
    this.searchText = '';
    this.searchFilter = null;
    this.setupFilters();
    this.scheduleSelectionSync();
  }

  selectAllCodeRows(): void {
    this.acsCodesGrid?.gridApi?.selectAllRows();
  }

  backToSearch(): void {
    this.pasteMode = false;
    this.clearPastedCodeFilter();
  }

  applyPastedCodes(): void {
    const requested = parseAcsCodes(this.pastedCodesText);
    if (!requested.length || !this.options?.acsTestId || this.pasteBusy) {
      return;
    }
    const request = ++this.pasteRequest;
    const testId = this.options.acsTestId;
    this.pasteBusy = true;
    this.pasteSummary = '';
    this.pasteSubscription?.unsubscribe();
    this.pasteSubscription = this.planOfActionRepositoryService
      .getAcsCodes(testId, this.options.acsAreaOfOperationId, this.options.acsTaskId)
      .pipe(take(1))
      .subscribe(storage => {
        if (request !== this.pasteRequest || this.options.acsTestId !== testId) {
          this.pasteBusy = false;
          return;
        }
        const requestedSet = new Set(requested);
        const matches = (storage?.acsCodes || []).filter(code => requestedSet.has((code.AcsCode || '').toUpperCase()));
        const found = new Set(matches.map(code => (code.AcsCode || '').toUpperCase()));
        const missing = requested.filter(code => !found.has(code));
        this.pastedCodeIds = matches.map(code => code.AcsCodeId);
        this.pasteSelectionActive = true;
        // Keep existing selections, including rows outside the pasted-code filter.
        const selected = new Map((this.options.acsCodes || []).map(code => [code.AcsCodeId, code]));
        matches.forEach(code => selected.set(code.AcsCodeId, code));
        this.options.acsCodes = Array.from(selected.values());
        this.options.questions = [];
        this.pasteSummary = `${found.size} of ${requested.length} codes matched.` +
          (missing.length ? ` Not found in this ACS test or current area/task: ${missing.join(', ')}.` : '');
        this.pasteBusy = false;
        this.onOptionChange();
        this.scheduleSelectionSync();
      }, () => {
        this.pasteBusy = false;
        this.pasteSummary = 'Unable to load ACS codes. Please try again.';
      });
  }

  clearPastedCodeFilter(): void {
    ++this.pasteRequest;
    this.pasteSubscription?.unsubscribe();
    this.pasteBusy = false;
    this.pastedCodeIds = null;
    this.pastedCodesText = '';
    this.pasteSummary = '';
    this.setupFilters();
    this.scheduleSelectionSync();
  }

  clearAllCodeSelections(): void {
    this.selectionSync = true;
    try {
      this.acsCodesGrid?.gridApi?.unselectAllRows();
    } finally {
      this.selectionSync = false;
    }
    this.options.acsCodes = [];
    this.options.questions = [];
    this.onOptionChange();
    this.scheduleSelectionSync();
  }

  // CAP exposes selection of all loaded rows, but no selection-by-key API.
  // Keep this AG Grid adapter local to the selector; never alter CAP's shared search.
  private getSelectionApi(): GridApi {
    return (this.acsCodesGrid?.gridApi as any)?.gridApi;
  }

  private scheduleSelectionSync(): void {
    if (!this.pasteSelectionActive) {
      return;
    }
    const api = this.getSelectionApi();
    if (api && api !== this.selectionApi) {
      this.selectionApi?.removeEventListener('modelUpdated', this.modelUpdated);
      this.selectionApi = api;
      api.addEventListener('modelUpdated', this.modelUpdated);
    }
    this.selectionRestorePending = true;
    clearTimeout(this.selectionTimer);
    this.selectionTimer = setTimeout(() => {
      if (!this.pasteSelectionActive || this.options.useQuestionBank) {
        return;
      }
      const selected = new Set((this.options.acsCodes || []).map(code => code.AcsCodeId));
      this.selectionSync = true;
      try {
        this.getSelectionApi()?.forEachNode(node => {
          if (node.data) {
            node.setSelected(selected.has(node.data.AcsCodeId));
          }
        });
      } finally {
        this.selectionSync = false;
        this.selectionRestorePending = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.selectionApi?.removeEventListener('modelUpdated', this.modelUpdated);
    clearTimeout(this.searchTimer);
    this.searchSubscription?.unsubscribe();
    ++this.pasteRequest;
    this.pasteSubscription?.unsubscribe();
    clearTimeout(this.selectionTimer);
  }
  // @Input() planOfAction: PlanOfActionEntity;
  // @Input() planOfActionSection: PlanOfActionSectionEntity;

  message: DwMessage;

  @Input() options: GenerateCodesOptions;
  @Output() optionsChange = new EventEmitter<GenerateCodesOptions>();

  acsTest: AcsTestEntity;

  allAcsCodes: AcsCodeStorage;

  aosCodesItem = AppMetaDataItemNames.AcsCode;

  codesGridFilters: any = {};
  questionGridFilters: any = {};

  codesGridPreferences: Partial<DwMetaDataGridPreferences> = {};
  questionsGridPreferences: Partial<DwMetaDataGridPreferences> = {};
  setupComplete: boolean = false;
  //acsCodeStatuses: AcsCodeStatuses;
  gridCallbacks: DwMdGridCallbacks;

  constructor(
    dwExpressionService: DwExpressionService,
    dwUiConfigRegistryService: DwUiConfigRegistryService,
    @Inject(DwUIMetaDataConfigToken) uiMetaDataConfig: DwUIMetaDataConfig,
    @Inject(DwMetaDataServiceToken) private selectorMetaDataService: DwMetaDataService,
    @Optional() private dwMetaDataFormStateService: DwMetaDataFormStateService,
    private planOfActionRepositoryService: PlanOfActionRepositoryService,
    private planOfActionGeneratorSection: PlanOfActionGeneratorService,
    private textSearchService: DwAllTextFieldFilterService = null
  ) {

  }

  ngOnInit(): void {
    this.setupData();

    
    this.gridCallbacks = {
      getRowClass: (row)=> this.getRowClass(row.data)
    }
  }

  ngOnChanges(changes: SimpleChanges) {

    if (changes.planOfAction?.firstChange === false || changes.planOfActionSection?.firstChange === false) {
      this.setupData();
    }
  }

  onCodeGridEvent(event: DwGridEvent) {
    if (event.eventType as string === 'gridDataLoad') {
      this.scheduleSelectionSync();
      return;
    }
    if (this.selectionSync || this.selectionRestorePending) {
      return;
    }
    if (event.eventType as string != 'rowSelectionChange') {
      return;
    }

    if (this.pasteSelectionActive) {
      const loadedIds = new Set<number>();
      this.getSelectionApi()?.forEachNode(node => {
        if (node.data) {
          loadedIds.add(node.data.AcsCodeId);
        }
      });
      const selections = new Map((this.options.acsCodes || [])
        .filter(code => !loadedIds.has(code.AcsCodeId)).map(code => [code.AcsCodeId, code]));
      (event.eventData || []).forEach(code => selections.set(code.AcsCodeId, code));
      this.options.acsCodes = Array.from(selections.values());
    } else {
      this.options.acsCodes = event.eventData;
    }
    this.options.questions = [];

    this.onOptionChange();
  }

  onQuestionGridEvent(event: DwGridEvent) {
    if (event.eventType as string != 'rowSelectionChange') {
      return;
    }
    this.options.acsCodes = [];
    this.pasteSelectionActive = false;
    this.options.questions = event.eventData;

    this.onOptionChange();
  }

  onOptionChange() {
    this.optionsChange.emit(this.options);

    this.setupFilters();
    this.scheduleSelectionSync();
  }

private getRowClass(acsCode: AcsCodeEntity) {
  if (!acsCode) {
    return '';
  }
  const acsStatus = this.options.acsCodeStatuses?.codesById[acsCode.AcsCodeId];

  if (acsStatus) {
    if (acsStatus.missing) {
      return 'dw-red';
    }
    return 'dw-green';
  }

  return '';
}

  private setupData() {
    // this.filters.AcsTestId = this.planOfAction.AcsTestId;

    this.codesGridPreferences = this.buildGridPreferences(false);
    this.questionsGridPreferences = this.buildGridPreferences(true);

    if (!this.options) {
      this.options = {
        acsCodes:[],
        questions:[],
        allowQuestionSelect: true,
        useQuestionBank: true,
        acsCodeStatuses: null,
      }

      this.optionsChange.emit(this.options);
    }
    this.setupFilters();

    //this.acsCodeStatuses = this.planOfActionRepositoryService.getAcsCodeStatus(this.planOfAction);

    this.setupComplete = true;
  }

  showOnlyMissedAcsCodes = false;
  setupFilters() {
    if (!this.options) {
      return;
    }
    this.selectionRestorePending = this.pasteSelectionActive && !this.options.useQuestionBank;

    this.codesGridFilters = {};
    this.questionGridFilters = {}
    // CAP merges external filters with previous filters. Explicitly clear owned
    // keys, otherwise returning to search leaves the old pasted-ID restriction.
    this.codesGridFilters[AppMetaData.Queries.AcsCodesGrid.Attributes.AcsCodeId.fieldName] = { In: [] };
    this.codesGridFilters.GroupSelectorSearch = null;
    this.questionGridFilters.GroupSelectorSearch = null;
    if (this.searchFilter) {
      this.codesGridFilters.GroupSelectorSearch = this.searchFilter;
      this.questionGridFilters.GroupSelectorSearch = this.searchFilter;
    }
    
    this.codesGridFilters[AppMetaData.Queries.AcsCodesGrid.Attributes.AcsAreaOfOperationAcsTestId.fieldName] = this.options.acsTestId;
    this.questionGridFilters[AppMetaData.Queries.AcsCodesWithQuestions.Attributes.AcsAreaOfOperationAcsTestId.fieldName] = this.options.acsTestId;;
    
    if (this.options.acsTaskId) {
      this.codesGridFilters[AppMetaData.Queries.AcsCodesGrid.Attributes.AcsAreaOfOperationTaskId.fieldName] = this.options.acsTaskId;
      this.questionGridFilters[AppMetaData.Queries.AcsCodesWithQuestions.Attributes.AcsAreaOfOperationTaskAcsAreaOfOperationTaskId.fieldName] = this.options.acsTaskId;
    } 

    if (this.options.acsAreaOfOperationId) {
      this.codesGridFilters[AppMetaData.Queries.AcsCodesGrid.Attributes.TaskAcsAreaOfOperationId.fieldName] = this.options.acsAreaOfOperationId;
      this.questionGridFilters[AppMetaData.Queries.AcsCodesWithQuestions.Attributes.AcsAreaOfOperationAcsAreaOfOperationId.fieldName] = this.options.acsAreaOfOperationId;
    }

    if (this.showOnlyMissedAcsCodes) {
      const missedCodes = this.getMissedAcsCodes();
      this.codesGridFilters[AppMetaData.Queries.AcsCodesGrid.Attributes.AcsCodeId.fieldName] = {
        In: missedCodes.length ? missedCodes : [-1]
      }
    }
    if (this.pastedCodeIds !== null) {
      const ids = this.showOnlyMissedAcsCodes ? this.pastedCodeIds.filter(id => this.getMissedAcsCodes().includes(id)) : this.pastedCodeIds;
      this.codesGridFilters[AppMetaData.Queries.AcsCodesGrid.Attributes.AcsCodeId.fieldName] = { In: ids.length ? ids : [-1] };
    }
  }

  private getMissedAcsCodes(): number[] {
    const missedCodes = [];

    for(let ascCodeId in (this.options.acsCodeStatuses?.codesById || {})) {
      const code = this.options.acsCodeStatuses.codesById[ascCodeId];

      if (code.missing) {
        missedCodes.push(code.ascCodeId);
      }
    }

    return missedCodes;
  }

  buttonClick(buttonConfig: DwButtonConfig): Observable<DwModalResult<any>> {
    let result = new DwModalResult<AcsCodeSelectionModalResult>();

    result.cancelled = false;

    switch (buttonConfig.command) {
      case DwButtonCommand.ok:
      case DwButtonCommand.saveAndClose:
      case DwButtonCommand.save:
        //this.generateSections();

        result.data = {
          GenerateCodesOptions: this.options,
        };

        result.closeModal = true;
        break;
      case DwButtonCommand.cancel:
        result.closeModal = true;
        result.cancelled = true;
    }

    return of(result);
  }

  // generateSections() {
  //   this.planOfActionGeneratorSection.generateQuestions(this.planOfActionSection, this.options);

  // }

  setMessage(message: string, severity: DwMessageSeverity = DwMessageSeverity.error) {
    this.message = {
      messageBody: message,
      severity: severity
    };
  }

  clearMessage() {
    this.message = null;
  }

  private buildGridPreferences(useQuestionBank: boolean): Partial<DwMetaDataGridPreferences> {
    const gridPrefs: Partial<DwMetaDataGridPreferences> = {
      gridStyle: {
        gridStyleName: 'Simple',
        gridHeightMode: DwGridHeightMode.fixedHeight,
        gridHeight: '200'
      },

      allowRowSelect: true,
      useCheckboxSelection: true,
      checkboxSelectionMode: DwGridCheckboxSelectionMode.checkboxAndRow,
      hideAdd: true,
      hideEditButton: false,
      hideDeleteButton: true,
      hideRefresh: true,
      hideExport: true,
      views: [],
    };

    const viewPref: Partial<DwMetaDataGridViewPreference> = {};

  
    if (useQuestionBank) {
      viewPref.metaDataQueryMeaning = AppMetaData.Queries.AcsCodesWithQuestions.QueryMeaning;
    } else {
      viewPref.metaDataQueryMeaning = AppMetaData.Queries.AcsCodesGrid.QueryMeaning
    }

    gridPrefs.views.push(viewPref as any);

    return gridPrefs;
  }
}
